import React, { useState, useEffect } from 'react';
import { CalendarDays, Clock } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Acne Treatment", "Pigmentation", "Hair Transplant", "Laser Treatment",
  "Botox", "Chemical Peeling", "Skin Diseases", "Scar Treatment", "Wrinkle Treatment"
];

export default function AppointmentSection() {
  const { t } = useLanguage();
  const [date, setDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (tomorrow.getDay() === 0) tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [timeSlot, setTimeSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [formData, setFormData] = useState({ name: '', phone: '', service: '' });
  const [submitting, setSubmitting] = useState(false);
  const [headerRef, headerVisible] = useScrollReveal(0.15);
  const [formRef, formVisible] = useScrollReveal(0.1);

  // Listen for treatment auto-select from ServicesSection or ChatBot
  useEffect(() => {
    const handler = (e) => {
      setFormData(prev => ({ ...prev, service: e.detail }));
    };
    window.addEventListener('selectTreatment', handler);
    return () => window.removeEventListener('selectTreatment', handler);
  }, []);

  // Fetch time slots when date changes
  useEffect(() => {
    if (!date) return;
    const fetchSlots = async () => {
      try {
        const dateStr = date.toISOString().split('T')[0];
        const res = await axios.get(`${API}/time-slots?date=${dateStr}`);
        setAvailableSlots(res.data.available_slots || []);
        setTimeSlot('');
      } catch (err) {}
    };
    fetchSlots();
  }, [date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service || !date || !timeSlot) return;
    setSubmitting(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const phone = formData.phone.replace(/\D/g, '').slice(0, 10);

      // Build WhatsApp message
      const msg = `*New Appointment Booked*%0A%0A*Patient:* ${formData.name}%0A*Phone:* ${phone}%0A*Treatment:* ${formData.service}%0A*Date:* ${dateStr}%0A*Time:* ${timeSlot}%0A%0A_Booked via DivyadeepClinic.com_`;
      const waUrl = `https://api.whatsapp.com/send?phone=918303365990&text=${msg}`;

      // Save to database
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: phone,
        service: formData.service,
        date: dateStr,
        time_slot: timeSlot
      });

      // Redirect to WhatsApp
      window.location.href = waUrl;
    } catch (err) {
      if (err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          alert(detail[0]?.msg || 'Please check your input');
        } else {
          alert('Please check your input and try again');
        }
      } else {
        alert('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <section id="appointment" className="py-20 md:py-32 bg-[#FDF1EC]/30" data-testid="appointment-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} ref={headerRef}>
          <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">Book Appointment</p>
          <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="appointment-title">
            {t('appointment_title')}
          </h2>
          <p className="mt-4 text-base text-[#475569] font-manrope">{t('appointment_subtitle')}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div ref={formRef} className={`bg-white rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-6 md:p-10 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-testid="appointment-form-container">
            <form onSubmit={handleSubmit} data-testid="appointment-form">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left - Form Fields */}
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-manrope font-medium text-[#0F172A] mb-2">{t('booking_name')}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-2xl bg-[#FAFAFA] border border-slate-200 text-sm font-manrope text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#D1A760] focus:ring-1 focus:ring-[#D1A760]/20 transition-all"
                      placeholder="Enter your full name"
                      data-testid="appt-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-manrope font-medium text-[#0F172A] mb-2">{t('booking_phone')}</label>
                    <div className="flex items-center gap-0 rounded-2xl bg-[#FAFAFA] border border-slate-200 focus-within:border-[#D1A760] focus-within:ring-1 focus-within:ring-[#D1A760]/20 transition-all">
                      <span className="pl-4 text-sm font-manrope text-[#475569] select-none">+91</span>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                        className="w-full px-2 py-3 bg-transparent text-sm font-manrope text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none"
                        placeholder="Phone Number"
                        data-testid="appt-phone-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-manrope font-medium text-[#0F172A] mb-2">{t('booking_service')}</label>
                    <Select value={formData.service} onValueChange={val => setFormData({...formData, service: val})}>
                      <SelectTrigger className="w-full px-4 py-3 h-auto rounded-2xl bg-[#FAFAFA] border-slate-200 text-sm font-manrope" data-testid="appt-service-select">
                        <SelectValue placeholder={t('select_service')} />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Time Slots */}
                  {availableSlots.length > 0 && (
                    <div>
                      <label className="block text-sm font-manrope font-medium text-[#0F172A] mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Select Time
                      </label>
                      <div className="grid grid-cols-3 gap-2" data-testid="time-slots-grid">
                        {availableSlots.map(slot => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setTimeSlot(slot)}
                            className={`px-3 py-2 rounded-xl text-xs font-manrope transition-all ${
                              timeSlot === slot
                                ? 'bg-[#0F172A] text-white'
                                : 'bg-[#FAFAFA] text-[#475569] border border-slate-200 hover:border-[#D1A760]'
                            }`}
                            data-testid={`time-slot-${slot.replace(/\s+/g, '-').replace(':', '')}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !formData.name || !formData.phone || !formData.service || !date || !timeSlot}
                    data-testid="appt-submit-btn"
                    className="w-full py-3.5 rounded-full bg-[#25D366] text-white font-manrope font-semibold text-sm hover:bg-[#1da851] hover:shadow-[0_4px_20px_rgba(37,211,102,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {submitting ? 'Booking...' : 'Confirm on WhatsApp'}
                  </button>
                </div>

                {/* Right - Calendar */}
                <div className="flex flex-col items-center" data-testid="appt-calendar-container">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarDays className="w-5 h-5 text-[#D1A760]" />
                    <p className="font-outfit font-medium text-[#0F172A]">{t('booking_date')}</p>
                  </div>
                  <div className="bg-[#FAFAFA] rounded-2xl p-4 border border-slate-200">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today || d.getDay() === 0;
                      }}
                      className="mx-auto"
                    />
                  </div>
                  {date && (
                    <p className="mt-3 text-sm text-[#475569] font-manrope">
                      Selected: <span className="text-[#0F172A] font-medium">{date.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
